// src/components/VerifyNFT.js

import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { styled } from '@mui/system';

// Define color constants based on Navbar and Home page styles
const COLORS = {
  buttonGradient: 'linear-gradient(to right, #9AC0CD, #4C5C64)', // Gradient similar to Navbar buttons
  buttonBoxShadow: '0 4px 15px rgba(154, 192, 205, 0.5)', // Same as Navbar buttons
  buttonHoverBackground: '#4C5C64',
  buttonHoverBoxShadow: '0 4px 20px rgba(154, 192, 205, 0.7)',
  buttonActiveBackground: '#5A6C45',
  cardBackground: 'rgba(0, 0, 0, 0.5)', // Dark Transparent
  cardBoxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  textPrimary: '#E0E4ED', // Off-White
  textSecondary: '#C2CCDF', // Light Blue
  chipBackground: '#4C5C64',
  chipText: '#E0E4ED',
  chipBorder: '#9AC0CD',
};

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor: COLORS.cardBackground,
  borderRadius: '16px',
  boxShadow: COLORS.cardBoxShadow,
  padding: theme.spacing(3),
  width: '100%',
  maxWidth: '800px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: COLORS.buttonGradient,
  color: '#FFFFFF',
  borderRadius: '20px',
  boxShadow: COLORS.buttonBoxShadow,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: COLORS.buttonHoverBackground,
    boxShadow: COLORS.buttonHoverBoxShadow,
  },
  '&:active': {
    backgroundColor: COLORS.buttonActiveBackground,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: COLORS.chipBackground,
  color: COLORS.chipText,
  borderColor: COLORS.chipBorder,
}));


const VerifyNFT = ({ discordUserId, guildId, threadId, wallets = [], onUpdateWallet, userRoles = [] }) => {
  const { jwtToken } = useContext(AuthContext); // Get jwtToken from AuthContext if needed

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error' | 'warning' | 'info'
  });
  const [uniqueAssignedRoles, setUniqueAssignedRoles] = useState([]); // New state for overall roles

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleVerify = async () => {
    const walletsArray = Array.isArray(wallets) ? wallets : [];

    if (walletsArray.length === 0) {
      setSnackbar({
        open: true,
        message: 'No wallets to verify.',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);

    console.log('Verifying wallets with jwtToken:', jwtToken);
    console.log('Verifying wallets with threadId:', threadId);
    // Update all wallets to 'verifying' status
    walletsArray.forEach((wallet) => {
      onUpdateWallet(wallet.address, { verificationStatus: 'verifying', reason: null, assignedRoles: [] });
    });

    try {
      // Send verification request to backend
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/verify-wallet`,
        {
          jwtToken: jwtToken, // Include jwtToken in the request body
          threadId: threadId, // Ensure threadId is correctly managed
        },
        {
          headers: {
            'x-discord-user-id': discordUserId,
            'x-guild-id': guildId,
            // No need to send Authorization header if jwtToken is in the body
          },
        }
      );

      if (response.status === 200) {
        const { message, results, rolesAssigned, individualRolesAssigned } = response.data;

        // Update each wallet based on results
        results.forEach((result) => {
          if (result.status === 'verified') {
            onUpdateWallet(result.walletAddress, {
              verificationStatus: 'verified',
              assignedRoles: result.assignedRoles, // Assign per-wallet roles
              reason: null,
            });
          } else {
            onUpdateWallet(result.walletAddress, {
              verificationStatus: 'failed',
              reason: result.reason,
              assignedRoles: [],
            });
          }
        });

        // Aggregate overall roles from both combined and individual roles
        const aggregatedRoles = Array.from(new Set([...rolesAssigned, ...individualRolesAssigned]));

        setUniqueAssignedRoles(aggregatedRoles); // Update the state for overall roles

        setSnackbar({
          open: true,
          message: message || 'Verification completed!',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Verification failed. Please try again.',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error during verification:', error.response?.data?.error || error.message);
      const errorMsg =
        error.response?.data?.error || 'An error occurred during verification.';
      setSnackbar({
        open: true,
        message: `Verification failed: ${errorMsg}`,
        severity: 'error',
      });

      // Update all wallets to 'failed' status
      walletsArray.forEach((wallet) => {
        onUpdateWallet(wallet.address, { verificationStatus: 'failed', reason: errorMsg, assignedRoles: [] });
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to aggregate unique roles across all wallets
  const aggregateUniqueRolesFromWallets = () => {
    const allRoles = wallets
      .filter(wallet => Array.isArray(wallet.assignedRoles))
      .flatMap(wallet => wallet.assignedRoles);
    // Remove duplicates using Set
    const uniqueRoles = Array.from(new Set(allRoles));
    return uniqueRoles;
  };

  const uniqueRolesFromWallets = aggregateUniqueRolesFromWallets();

  // Combine uniqueAssignedRoles (from verification) and uniqueRolesFromWallets and userRoles
  const overallAssignedRoles = Array.from(new Set([...uniqueAssignedRoles, ...uniqueRolesFromWallets, ...userRoles]));

  return (
    <Box mt={4} display="flex" flexDirection="column" alignItems="center">
      <StyledCard>
        {/* Instructional Text */}
        <Typography
          variant="body1"
          align="center"
          paragraph
          sx={{ color: COLORS.textSecondary }}
        >
          Click the button below to verify NFT ownership for all added wallets.
        </Typography>

        {/* Display a message prompting to add wallets if none exist */}
        {wallets.length === 0 && (
          <Box mt={2} textAlign="center">
            <Typography variant="h6" gutterBottom sx={{ color: COLORS.textPrimary }}>
              You have no wallets linked.
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
              Please use the "Add Wallet" button above to link your wallet.
            </Typography>
          </Box>
        )}

        {/* Verify All Wallets Button */}
        <Box display="flex" justifyContent="center" mt={2}>
          <StyledButton
            variant="contained"
            onClick={handleVerify}
            disabled={wallets.length === 0 || loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify All Wallets'}
          </StyledButton>
        </Box>


        {/* Display Overall Assigned Roles */}
        {overallAssignedRoles.length > 0 && (
          <Box mt={4} width="100%" textAlign="center">
            <Typography variant="h6" gutterBottom sx={{ color: COLORS.textPrimary }}>
              Verified Roles:
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
              {overallAssignedRoles.map((roleName, index) => (
                <StyledChip
                  key={`overall-${roleName}-${index}`}
                  label={roleName}
                />
              ))}
            </Stack>
          </Box>
        )}
      </StyledCard>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default VerifyNFT;


